import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Ban, Globe, Tags, Check, Plus, Loader2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  StatsBar,
  StatsBarId,
  StatsBarActivity,
} from '../../components/StatsBar'
import { AuthService } from '@/services/core/auth'
import { BlocklistsApi, BlocklistInput, Blocklist } from '@/services/rules/blocklistsApi'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { TeamsApi } from '@/services/users/teamsApi'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatDate } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { ResourceUsageCard } from '@/components/ResourceUsageCard'

// ── Inner form component ────────────────────────────────────────────────────

interface BlocklistEditFormProps {
  blocklist: Blocklist | null
  id: string
  isNew: boolean
}

function BlocklistEditForm({ blocklist, id, isNew }: BlocklistEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()
  const hasToken = AuthService.isAuthenticated()
  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })

  const [domainInput, setDomainInput] = useState('')
  const [categoryInput, setCategoryInput] = useState('')

  const form = useForm({
    defaultValues: {
      name: blocklist?.name ?? '',
      ownerTeamId: blocklist?.ownerTeamId ?? '',
      advertiserDomains: blocklist?.advertiserDomains ?? [] as string[],
      iabCategories: blocklist?.iabCategories ?? [] as string[],
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        ownerTeamId: value.ownerTeamId || undefined,
        advertiserDomains: value.advertiserDomains.length ? value.advertiserDomains : undefined,
        iabCategories: value.iabCategories.length ? value.iabCategories : undefined,
        iabCategoryAction: 'BLOCKED',
        objectState: (blocklist?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => BlocklistsApi.update(id!, { name: blocklist?.name ?? '', objectState }),
    ['blocklist', id],
    ['blocklists']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: BlocklistInput) =>
      isNew ? BlocklistsApi.create(id!, input) : BlocklistsApi.update(id!, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['blocklist', id], data)
      queryClient.setQueryData(['blocklists'], (old: Blocklist[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((b) => b.id === data.id)
        return idx >= 0 ? old.map((b) => (b.id === data.id ? data : b)) : [...old, data]
      })
      markClean()
      if (isNew) {
        navigate(`/publishing/blocklists/${id}`, { replace: !createDialog })
      }
      toast.success(t('common.saved'), { description: isNew ? t('blocklists.createdSuccess') : t('blocklists.savedSuccess'), duration: 3000 })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError ? error.message : isNew ? t('blocklists.createFailed') : t('blocklists.saveFailed')
      const detail = apiErrorDescription(error)
      toast.error(summary, { description: detail, duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('nav.blocklists'), href: '/publishing/blocklists' },
    { label: isNew ? t('blocklists.newTitle') : (blocklist?.name ?? t('blocklists.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack objectType={isNew ? undefined : t('objectTypes.blocklist')}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={blocklist?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.blocklist')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('blocklists.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {blocklist && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={blocklist!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {blocklist && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={blocklist.id} />,
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(blocklist.createTime)}
                    updated={formatDate(blocklist.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="ad-edit-columns">
          <div className="ad-edit-columns__info">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Ban} />
                  {t('blocklists.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('blocklists.ownerTeam')}</label>
                    <form.Field name="ownerTeamId">
                      {(field) => (
                        <Select label={t('blocklists.ownerTeam')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                          <SelectTrigger><SelectValue placeholder={t('blocklists.selectTeam')}>{teams.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__clear__">—</SelectItem>
                            {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card tone="attention">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Globe} />
                  {t('blocklists.advertiserDomains')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <form.Field name="advertiserDomains">
                      {(field) => (
                        <div className="flex flex-wrap gap-1.5 rounded-(--ad-radius-sm) border border-(--ad-border) bg-(--ad-surface) p-2 min-h-9.5">
                          {field.state.value.map((tag, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-(--ad-badge-blue-bg) text-(--ad-badge-blue) border border-(--ad-badge-blue-border) px-2 py-0.5 text-xs font-medium">
                              {tag}
                              <button type="button" onClick={() => field.handleChange(field.state.value.filter((_, j) => j !== i))} className="hover:text-(--ad-danger)">
                                <X className="size-3" />
                              </button>
                            </span>
                          ))}
                          <input
                            className="flex-1 min-w-30 bg-transparent outline-none text-sm text-(--ad-text) placeholder:text-(--ad-text-faint)"
                            value={domainInput}
                            onChange={(e) => setDomainInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && domainInput.trim()) {
                                e.preventDefault()
                                field.handleChange([...field.state.value, domainInput.trim()])
                                setDomainInput('')
                              }
                              if (e.key === 'Backspace' && !domainInput && field.state.value.length > 0) {
                                field.handleChange(field.state.value.slice(0, -1))
                              }
                            }}
                            placeholder={t('blocklists.domainsPlaceholder')}
                          />
                        </div>
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card tone="attention">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Tags} />
                  {t('blocklists.iabCategories')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <form.Field name="iabCategories">
                      {(field) => (
                        <div className="flex flex-wrap gap-1.5 rounded-(--ad-radius-sm) border border-(--ad-border) bg-(--ad-surface) p-2 min-h-9.5">
                          {field.state.value.map((tag, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-(--ad-badge-blue-bg) text-(--ad-badge-blue) border border-(--ad-badge-blue-border) px-2 py-0.5 text-xs font-medium">
                              {tag}
                              <button type="button" onClick={() => field.handleChange(field.state.value.filter((_, j) => j !== i))} className="hover:text-(--ad-danger)">
                                <X className="size-3" />
                              </button>
                            </span>
                          ))}
                          <input
                            className="flex-1 min-w-30 bg-transparent outline-none text-sm text-(--ad-text) placeholder:text-(--ad-text-faint)"
                            value={categoryInput}
                            onChange={(e) => setCategoryInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && categoryInput.trim()) {
                                e.preventDefault()
                                field.handleChange([...field.state.value, categoryInput.trim()])
                                setCategoryInput('')
                              }
                              if (e.key === 'Backspace' && !categoryInput && field.state.value.length > 0) {
                                field.handleChange(field.state.value.slice(0, -1))
                              }
                            }}
                            placeholder={t('blocklists.categoriesPlaceholder')}
                          />
                        </div>
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {!isNew && id && <ResourceUsageCard usageParam="blocklist" resourceId={id} />}
        </div>

        <div className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => cancelNew('/publishing/blocklists')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
            >
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('blocklists.createBlocklist') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function BlocklistEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const { data: blocklist, isLoading, isError } = useQuery({
    queryKey: ['blocklist', id],
    queryFn: () => BlocklistsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('nav.blocklists'), href: '/publishing/blocklists' },
    { label: isNew ? t('blocklists.newTitle') : t('blocklists.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return (
    <BlocklistEditForm
      key={id}
      blocklist={blocklist ?? null}
      id={id!}
      isNew={isNew}
    />
  )
}
